import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {transactionService} from '@/services/transactionService';
import type {
  AdminPaginatedEnvelope,
  AfriexAdminTransaction,
  AfriexQueryParams,
  AfriexTransactionStatus,
} from '@/types';
import {formatAdminTransaction} from '@/utils/dashboard';

import type {TransactionListProps} from './types';

const useUserTransactionList = (userId: string): TransactionListProps => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;

  const [transactionList, setTransactionList] = useState<
    AfriexAdminTransaction[]
  >([]);
  // Start in loading state so the first paint cannot present an empty table
  // before the mount effect has even issued the customer-list request.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearch] = useState<boolean>(false);
  const [transactionTotal, setTransactionTotal] = useState<number>(0);
  const [showPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [exportFilters, setExportFilters] = useState<AfriexQueryParams>({});
  const [transactionSummary, setTransactionSummary] =
    useState<AdminPaginatedEnvelope<AfriexAdminTransaction[]>['summary']>();
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated the fetch callback and fired a
  // second, identical request on every mount. `currentFilters` now holds only
  // the filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchAllUserTransactions = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);

        const query = {...params, ...effectiveFilters};
        const {data, total, summary} =
          await transactionService.getUserTransactionList(userId, query);
        const {page: _page, limit: _limit, ...filters} = query;
        setExportFilters(filters);

        const filtered = await Promise.all(
          data &&
            data.map(async (transaction) => {
              const at = await formatAdminTransaction(transaction);
              return {
                ...transaction,
                type: at.type,
                adminUser: at.adminUser,
              };
            }),
        );

        setTransactionList(filtered);
        setTransactionTotal(total || 0);
        setTransactionSummary(summary);
      } catch (error) {
        // Drop the previous page's rows so they do not render under the error.
        setTransactionList([]);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveFilters, userId],
  );

  useEffect(() => {
    // Fire-and-forget: fetchAllUserTransactions catches its own failures into
    // `error`, so this only stops an unexpected throw becoming an unhandled
    // rejection.
    fetchAllUserTransactions({}).catch(() => {});
  }, [fetchAllUserTransactions]);

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);

    // Pagination reset is success-only work. See useTransactionList.
    if (applied) {
      setIsPageReset((prev) => !prev);
    }

    return applied;
  }, [applyFilters, filtersToApply]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        setFilterCount(Object.keys(updatedFilters).length);
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | number | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Count only filters that are actually set. This screen renders the
        // same TransactionFilters panel as useTransactionList, so the rule has
        // to match or Apply behaves differently on /transactions/user/:userId
        // than on /transactions for identical input.
        const filteredCount =
          Object.values(updatedFilters).filter(Boolean).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };

  return {
    error,
    fetchTransactionList: fetchAllUserTransactions,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: transactionTotal,
      summary: transactionSummary,
    },
    showPagination,
    transactionList,
    exportFilters,
    filterCount,
    isPageReset,
    currentFilters,
    selectCurrentFilters,
    filtersToApply,
    selectAppliedFilters,
    handleApplyFilters,
    clearFilters: cleanFilters,
  };
};

export default useUserTransactionList;
